/* Theme context — persists user preference to localStorage and applies
 * a `light-theme` class on <html>. The app is dark-first (matching the
 * original design), so the DEFAULT is dark. Setting light mode adds a
 * class that our index.css overrides for the hardcoded dark palette.
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'yvf.theme';   // 'light' | 'dark'
const DEFAULT_THEME = 'dark';

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  toggleTheme: () => {},
});

const applyTheme = (theme) => {
  const root = document.documentElement;
  if (theme === 'light') {
    root.classList.add('light-theme');
    root.setAttribute('data-theme', 'light');
  } else {
    root.classList.remove('light-theme');
    root.setAttribute('data-theme', 'dark');
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_THEME;
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME;
  });

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (e) {
      // Storage can be blocked in incognito / SSR / quota-exceeded. Theme
      // still works for the session — it just won't persist across reloads.
      console.debug('ThemeContext: localStorage.setItem blocked, theme will not persist', e?.message);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [theme, setTheme]);

  const contextValue = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
